import React, { useState, useRef } from 'react';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { Dialog } from 'primereact/dialog';
import { FileUpload, FileUploadHandlerEvent } from 'primereact/fileupload';
import { adminApi } from '../api/admin';
import axios from 'axios';

interface BackupMessage {
  type: 'success' | 'error' | 'info' | 'warn';
  text: string;
}

const DatabaseBackup: React.FC = () => {
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [message, setMessage] = useState<BackupMessage | null>(null);
  const [showRestoreDialog, setShowRestoreDialog] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const fileUploadRef = useRef<FileUpload>(null);

  const handleDownloadBackup = async () => {
    setDownloadLoading(true);
    setMessage(null);

    try {
      const metadata = await adminApi.downloadDatabaseBackup();

      const sizeInMB = (metadata.size / 1024 / 1024).toFixed(2);
      setMessage({
        type: 'success',
        text: `Backup downloaded successfully: ${metadata.filename} (${sizeInMB} MB)`,
      });
    } catch (error) {
      console.error('Backup download failed:', error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          setMessage({
            type: 'error',
            text: 'Rate limit exceeded. You can only generate 3 backups per hour. Please try again later.',
          });
        } else if (error.response?.status === 503) {
          setMessage({
            type: 'error',
            text: 'Database backup utility is not available. Please contact the system administrator.',
          });
        } else if (error.response?.status === 403) {
          setMessage({
            type: 'error',
            text: 'You do not have permission to generate database backups.',
          });
        } else if (error.response?.data) {
          try {
            const errorText = await error.response.data.text();
            const errorData = JSON.parse(errorText);
            setMessage({
              type: 'error',
              text: errorData.error?.message || 'Failed to generate database backup.',
            });
          } catch {
            setMessage({
              type: 'error',
              text: 'Failed to generate database backup. Please try again later.',
            });
          }
        } else {
          setMessage({
            type: 'error',
            text: error.message || 'Failed to generate database backup. Please try again later.',
          });
        }
      } else {
        setMessage({
          type: 'error',
          text: 'An unexpected error occurred. Please try again later.',
        });
      }
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleFileSelect = (event: FileUploadHandlerEvent) => {
    if (event.files && event.files.length > 0) {
      const file = event.files[0];

      // Validate file extension
      if (!file.name.endsWith('.sql.gz') && !file.name.endsWith('.gz')) {
        setMessage({
          type: 'error',
          text: 'Invalid file format. Please select a .sql.gz backup file.',
        });
        fileUploadRef.current?.clear();
        return;
      }

      // Validate file size (max 500MB)
      if (file.size > 500 * 1024 * 1024) {
        setMessage({
          type: 'error',
          text: 'File is too large. Maximum file size is 500MB.',
        });
        fileUploadRef.current?.clear();
        return;
      }

      setSelectedFile(file);
      setShowConfirmDialog(true);
      fileUploadRef.current?.clear();
    }
  };

  const handleRestoreConfirm = async () => {
    if (!selectedFile) return;

    setShowConfirmDialog(false);
    setRestoreLoading(true);
    setMessage(null);

    try {
      const result = await adminApi.restoreDatabaseBackup(selectedFile);

      setMessage({
        type: 'success',
        text: `Database restored successfully from ${result.restoredFrom}. Safety backup created: ${result.safetyBackup}`,
      });
      setSelectedFile(null);
      setShowRestoreDialog(false);
    } catch (error) {
      console.error('Database restore failed:', error);

      if (axios.isAxiosError(error)) {
        if (error.response?.status === 429) {
          setMessage({
            type: 'error',
            text: 'Rate limit exceeded. You can only restore once per hour. Please try again later.',
          });
        } else if (error.response?.status === 503) {
          setMessage({
            type: 'error',
            text: 'Database restore utility is not available. Please contact the system administrator.',
          });
        } else if (error.response?.status === 400) {
          const errorData = error.response?.data;
          setMessage({
            type: 'error',
            text: errorData?.error?.message || 'Invalid backup file. Please check the file and try again.',
          });
        } else {
          const errorData = error.response?.data;
          setMessage({
            type: 'error',
            text: errorData?.error?.message || 'Failed to restore database. Please try again later.',
          });
        }
      } else {
        setMessage({
          type: 'error',
          text: 'An unexpected error occurred. Please try again later.',
        });
      }
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleRestoreCancel = () => {
    setShowConfirmDialog(false);
    setSelectedFile(null);
  };

  return (
    <div data-component="DatabaseBackup">
      <Card className="mb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Database Backup
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Download a compressed SQL backup of the entire database.
              This will include all members, payments, invoices, and other data.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              <i className="pi pi-info-circle mr-1"></i>
              Backups are limited to 3 per hour to prevent system overload.
            </p>
          </div>
          <Button
            label="Download Backup"
            icon={downloadLoading ? 'pi pi-spin pi-spinner' : 'pi pi-download'}
            onClick={handleDownloadBackup}
            disabled={downloadLoading || restoreLoading}
            className="p-button-primary"
            loading={downloadLoading}
          />
        </div>

        {message && (
          <div className="mt-4">
            <Message
              severity={message.type === 'success' ? 'success' : message.type === 'error' ? 'error' : message.type === 'warn' ? 'warn' : 'info'}
              text={message.text}
              className="w-full"
            />
          </div>
        )}
      </Card>

      <Card className="mb-4">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-red-600 mb-2">
              <i className="pi pi-exclamation-triangle mr-2"></i>
              Database Restore
            </h3>
            <p className="text-sm text-gray-600 mb-2">
              <strong>⚠️ WARNING: This operation will replace ALL current data in the database.</strong>
            </p>
            <p className="text-sm text-gray-600 mb-4">
              Upload a backup file to restore the database. A safety backup will be created automatically before restore.
            </p>
            <p className="text-xs text-gray-500 mb-4">
              <i className="pi pi-info-circle mr-1"></i>
              Restores are limited to 1 per hour. Maximum file size: 500MB.
            </p>
          </div>
          <Button
            label="Restore Database"
            icon="pi pi-upload"
            onClick={() => setShowRestoreDialog(true)}
            disabled={downloadLoading || restoreLoading}
            className="p-button-danger"
            severity="danger"
          />
        </div>
      </Card>

      <Card>
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          Backup Information
        </h3>
        <div className="space-y-2 text-sm text-gray-600">
          <div className="flex items-start">
            <i className="pi pi-check-circle text-green-500 mr-2 mt-1"></i>
            <div>
              <strong>Format:</strong> Compressed SQL dump (gzip)
            </div>
          </div>
          <div className="flex items-start">
            <i className="pi pi-check-circle text-green-500 mr-2 mt-1"></i>
            <div>
              <strong>Contents:</strong> All database tables, schema, and data
            </div>
          </div>
          <div className="flex items-start">
            <i className="pi pi-check-circle text-green-500 mr-2 mt-1"></i>
            <div>
              <strong>Filename:</strong> memberhub_backup_YYYYMMDD_HHMMSS.sql.gz
            </div>
          </div>
          <div className="flex items-start">
            <i className="pi pi-shield text-blue-500 mr-2 mt-1"></i>
            <div>
              <strong>Safety:</strong> Automatic backup created before restore
            </div>
          </div>
        </div>
      </Card>

      {/* Restore Dialog */}
      <Dialog
        header="Restore Database from Backup"
        visible={showRestoreDialog}
        style={{ width: 'min(95vw, 500px)' }}
        onHide={() => setShowRestoreDialog(false)}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={() => setShowRestoreDialog(false)}
              className="p-button-text"
              disabled={restoreLoading}
            />
          </div>
        }
      >
        <div data-component="RestoreDialog">
          <Message
            severity="warn"
            text="Uploading a backup will replace ALL current database data. A safety backup will be created first."
            className="w-full mb-4"
          />

          <FileUpload
            ref={fileUploadRef}
            mode="basic"
            name="backup"
            accept=".gz,.sql.gz"
            maxFileSize={500 * 1024 * 1024}
            customUpload
            uploadHandler={handleFileSelect}
            auto
            chooseLabel="Choose Backup File"
            disabled={restoreLoading}
          />
        </div>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        header="⚠️ Confirm Database Restore"
        visible={showConfirmDialog}
        style={{ width: 'min(95vw, 500px)' }}
        onHide={handleRestoreCancel}
        footer={
          <div>
            <Button
              label="Cancel"
              icon="pi pi-times"
              onClick={handleRestoreCancel}
              className="p-button-text"
              disabled={restoreLoading}
            />
            <Button
              label={restoreLoading ? 'Restoring...' : 'Restore Database'}
              icon={restoreLoading ? 'pi pi-spin pi-spinner' : 'pi pi-check'}
              onClick={handleRestoreConfirm}
              className="p-button-danger"
              loading={restoreLoading}
              disabled={restoreLoading}
            />
          </div>
        }
      >
        <div data-component="ConfirmDialog">
          <Message
            severity="error"
            text="THIS WILL PERMANENTLY REPLACE ALL CURRENT DATA!"
            className="w-full mb-4"
          />

          <div className="mb-4">
            <p className="text-sm text-gray-700 mb-2">
              <strong>Selected file:</strong> {selectedFile?.name}
            </p>
            <p className="text-sm text-gray-700 mb-2">
              <strong>File size:</strong> {selectedFile ? (selectedFile.size / 1024 / 1024).toFixed(2) : 0} MB
            </p>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded p-3 mb-4">
            <p className="text-sm text-yellow-800 mb-2">
              <strong>Before proceeding:</strong>
            </p>
            <ul className="text-sm text-yellow-800 list-disc list-inside space-y-1">
              <li>A safety backup will be created automatically</li>
              <li>All users will be disconnected</li>
              <li>The restore may take several minutes</li>
              <li>You should have tested this backup file</li>
            </ul>
          </div>

          <p className="text-sm text-gray-600">
            Are you sure you want to restore the database from this backup?
          </p>
        </div>
      </Dialog>
    </div>
  );
};

export default DatabaseBackup;
